import * as R from 'ramda'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import Loading from '../components/loading'
import {
  BLOG_STATUS,
  BLOG_STATUS_TRANSITION_TYPE
} from '../constants/enums/blog.enum.constant'
import { BLOG_DETAIL_PAGE_TYPE } from '../constants/enums/common.enum.constant'
import BlogDetailTitleBar from '../containers/blog/BlogDetailTitleBar'
import BlogForm from '../containers/blog/BlogForm'
import HorizontalGridDivider from '../containers/horizontalGridDivider'
import SideMenu from '../containers/sideMenu'
import StyledMain from '../containers/styledMain'
import TopBar from '../containers/topBar'
import VerticalGridDivider from '../containers/verticalGridDivider'
import { BlogDetailInterface, BlogFormInterface } from '../interfaces/blog'
import {
  createBlogRequest,
  getBlogDetailRequest,
  updateBlogRequest,
  updateBlogStatusRequest
} from '../redux/blog/actions'
import {
  getBlogDetail,
  getIsCreateOrUpdatingBlog,
  getIsFetchingBlogDetail
} from '../redux/blog/selectors'
import { createAndUpdateBlogFormToApiConvertor } from '../utils/service'

const emptyBlogDetail: BlogDetailInterface = {
  blogId: '',
  slugName: '',
  title: '',
  subtitle: '',
  content: '',
  status: BLOG_STATUS.draft
}

const BlogDetail = ({
  match: {
    params: { blogId: id }
  }
}: {
  match: {
    params: {
      blogId: string
    }
  }
  [key: string]: any
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()

  const checkPageType = (
    type: keyof typeof BLOG_DETAIL_PAGE_TYPE
  ): ((path: string) => boolean) =>
    R.compose(R.equals(type) as any, R.head, R.reverse as any, R.split('/'))
  const disableForm = checkPageType(BLOG_DETAIL_PAGE_TYPE.view)(pathname)
  const newForm = checkPageType(BLOG_DETAIL_PAGE_TYPE.create)(pathname)

  const blogDetail = newForm
    ? emptyBlogDetail
    : (useSelector(getBlogDetail) as BlogDetailInterface)
  if (blogDetail?.status === BLOG_STATUS.archived && !disableForm) {
    history.push(`/blog/${blogDetail.blogId}/view`)
  }

  if (blogDetail?.status !== BLOG_STATUS.archived && disableForm) {
    history.push(`/blog/${blogDetail.blogId}/edit`)
  }

  const isFetchingBlogDetail = useSelector(getIsFetchingBlogDetail)
  const isCreateOrUpdatingBlog = useSelector(getIsCreateOrUpdatingBlog)

  const formHandlers = {
    createBlog: (createdBlog: BlogFormInterface): void => {
      dispatch(
        createBlogRequest(createAndUpdateBlogFormToApiConvertor(createdBlog))
      )
    },
    updateBlog: (updatedBlog: BlogFormInterface): void => {
      dispatch(
        updateBlogRequest({
          blogId: blogDetail.blogId,
          blogDetail: createAndUpdateBlogFormToApiConvertor(updatedBlog)
        })
      )
    },
    statusChangeHandlerGenerator: (
      action: keyof typeof BLOG_STATUS_TRANSITION_TYPE
    ) => () => {
      dispatch(
        updateBlogStatusRequest({
          blogId: blogDetail.blogId,
          action
        })
      )
    }
  }

  useEffect(() => {
    if (!newForm) {
      dispatch(getBlogDetailRequest(id))
    }
  }, [])

  return (
    <HorizontalGridDivider>
      <SideMenu />
      <VerticalGridDivider>
        <TopBar />
        <StyledMain>
          <BlogDetailTitleBar
            title={'Blog Detail'}
            detail={blogDetail}
            newForm={newForm}
          />
          {isFetchingBlogDetail || isCreateOrUpdatingBlog ? (
            <Loading />
          ) : (
            <BlogForm
              blogDetail={blogDetail}
              formHandlers={formHandlers}
              disableForm={disableForm}
              newForm={newForm}
            />
          )}
        </StyledMain>
      </VerticalGridDivider>
    </HorizontalGridDivider>
  )
}

export default BlogDetail
